<template>
  <div>
    <collection-history v-if="isHistoryView" @isHistoryView="isHistoryView = $event" :collectionPayload="collectionPayload"/>
    <dimmed v-if="isHeatmapRun">
      <alert
          :icon="'icon--heatmap-confirm'"
          :title="$t('heatmap.confirm.run.title')"
          :description="selectedState !== 'stop' ? $t('heatmap.confirm.run.description') : ''"
          :submit="$t('common.confirm')"
          :cancel="$t('common.cancel')"
          @onCancel="isHeatmapRun = false;selectNode = null"
          @onSubmit="stateUpdate(true)" />
    </dimmed>

    <dimmed v-if="isHeatmapStop">
      <alert
          :icon="'icon--heatmap-confirm'"
          :title="$t('heatmap.confirm.stop.title')"
          :description="$t('heatmap.confirm.stop.description')"
          :submit="$t('common.confirm')"
          :cancel="$t('common.cancel')"
          @onCancel="isHeatmapStop = false;selectNode = null"
          @onSubmit="stateUpdate(false)" />
    </dimmed>
    <div class="list">
      <div class="thead">
        <table>
          <colgroup>
            <col style="width: 25px;">
            <col>
            <col style="width: 225px;">
            <col style="width: 130px;">
            <col style="width: 123px;">
            <col style="width: 108px;">
          </colgroup>
          <thead>
          <tr>
            <th class="gap"></th>
            <th v-for="(column, idx) in columns"
                :key="`tag-list-${column.text}-${column.value}`"
                :class="[{ active: listData.length && computedSort === column.value }, `th-${column.value}`]"
                @click="changeSort(column.value)"
            >
              {{ column.text }}
              <button class="sort" v-if="![0,4].includes(idx)"
                      :class="{ active: listData.length && computedSort === column.value,
                                asc: order,
                                desc: !order }">
                <i class="icon icon--sort-asc"></i>
                <i class="icon icon--sort-desc"></i>
              </button>
            </th>
          </tr>
          </thead>
        </table>
      </div>
      <div v-if="listData.length > 0" class="tbody" :class="{ shadow: listData.length > 0 }" >
        <table>
          <colgroup>
            <col style="width: 25px;">
            <col >
            <col style="width: 225px;">
            <col style="width: 130px;">
            <col style="width: 123px;">
            <col style="width: 108px;">
          </colgroup>
          <tbody>
          <template v-for="(pageData,idx) in listData">
            <tr :key="idx">
              <td class="gap"></td>
              <td>
                <div class="page-td">
                  <div class="title">
                    <div v-if="pageData.title && pageData.title.trim()" class="name">
                          <span>
                            <span class="before">
                              <text-highlight :searchKeyword="keyword"
                                              :textItem="pageData.title.substr(0, pageData.title.length - 6)"
                                              :backItem="pageData.title.substr(-6, 6)">
                              </text-highlight></span>
                            <span class="after">
                              <text-highlight :searchKeyword="keyword"
                                              :frontItem="pageData.title.substr(0, pageData.title.length - 6)"
                                              :textItem="pageData.title.substr(-6, 6)">
                              </text-highlight>
                            </span>
                          </span>
                    </div>
                    <span v-else>
                        <span class="before">
                          {{ pageData.title.substr(0, pageData.title.length - 6).replace(/ /g, '&nbsp;') }}</span><span class="after">{{ pageData.title.substr(-6, 6).replace(/ /g, '&nbsp;') }}
                        </span>
                      </span>
                    <div class="url">
                      <a :href="pageData.listUrl"
                         :title="pageData.listUrl"
                         target="_blank">
                        <i class="icon icon--ba-link"></i>
                        <span class="url__wrap">
                            <span class="before">
                              <text-highlight :searchKeyword="keyword"
                                              :textItem="pageData.listUrl.substr(0, pageData.listUrl.length - 6)"
                                              :backItem="pageData.listUrl.substr(-6, 6)">
                              </text-highlight></span><span class="after"><text-highlight :searchKeyword="keyword"
                                                                                          :frontItem="pageData.listUrl.substr(0, pageData.listUrl.length - 6)"
                                                                                          :textItem="pageData.listUrl.substr(-6, 6)">
                              </text-highlight>
                            </span>
                          </span>
                      </a>
                    </div>
                  </div>
                </div>
              </td>
              <td class="td-heatmap" :class="{disabled: isDemo}">
                <ui-heatmap-state-icon
                    :idx="idx"
                    :pageData="pageData"
                    :disabled="isOldCode"
                    @selectNode="selectNode = $event"
                    @selectedState="selectedState = $event"
                    @isHeatmapRun="isHeatmapRun = $event"
                    @isHeatmapStop="isHeatmapStop = $event"
                    @isHeatmapSet="isHeatmapSet"
                    @onHeatmapRegister="onHeatmapRegister"
                />
              </td>
              <td class="td-date">{{ pageData.startDate | dashDate }}</td>
              <td class="td-date" v-if="pageData.endDate">{{ pageData.endDate | dashDate }}</td>
              <td class="td-date noDate" v-else>{{ $t('common.nonStop') }}</td>
              <td class="td-history">
                <button class="history" @click="onHistoryView(pageData)">{{ $t('group.column.view') }}</button>
              </td>
            </tr>
          </template>
          </tbody>
        </table>
      </div>
      <div v-else class="no-data">
        <i class="icon loading--empty"></i>
        <p class="no-data-title" :class="{ search: isHeatmapExist }">
          {{ !isHeatmapExist ? $t('common.noHeatmapResult') : $t('common.noResult') }}
        </p>
        <template v-if="keyword">
          <p class="no-data-description">
            {{ $t('common.noResultSearch') }}
          </p>
          <p class="no-data-keyword">
            '{{ keyword }}'
          </p>
        </template>
      </div>
    </div>
    <div class="list-bottom">
      <ui-navigation class="page-navigation"
                     :disabled="listData.length === 0"
                     :currentPage="listOption.pageNum"
                     :lastPage="lastPage"
                     @setPage="setPage" />
      <span class="view-per-page">
        {{ $t('common.pagination.viewPerPage') }}
        <ui-selectbox :value="listOption.itemsPerPage"
                      :items="viewPerPageOptionItems"
                      :width="'78px'"
                      :height="'32px'"
                      :slideUp="true"
                      @input="onViewPerPageOptionChange" />
      </span>
    </div>
  </div>
</template>
<script>
import { getHeatmapGroupList } from '@/api/group'
import { updateBaApply } from '@/api/heatmap'
import { HEATMAP_STATE } from '@/libs/constants'
import { normalizeHeatmapListData } from '@/libs/heatmap-data-normalizer'
import { simpleUrl } from '@/libs/utils'
import { format } from 'date-fns'
import CollectionHistory from '@/components/group/CollectionHistory'
import UiHeatmapStateIcon from '@/components/ui/UiHeatmapStateIcon'
import Dimmed from '@/components/ui/Dimmed'
import Alert from '@/components/ui/Alert.vue'
import TextHighlight from '@/components/ui/TextHighlight'
import UiNavigation from '@/components/ui/UiNavigation'
import UiSelectbox from '@/components/ui/UiSelectbox'
import { mapState } from 'vuex'

export default {
  components: {
    CollectionHistory,
    UiHeatmapStateIcon,
    Dimmed,
    UiSelectbox,
    UiNavigation,
    TextHighlight,
    Alert
  },
  props: [
    'searchOption',
    'keyword',
    'count',
    'sid',
    'searchLength'
  ],
  data () {
    return {
      isLoadedOnce: false,
      isHistoryView: false,
      viewPerPageOptionItems: [
        {
          text: '100',
          value: 100
        },
        {
          text: '30',
          value: 30
        },
        {
          text: '20',
          value: 20
        },
        {
          text: '10',
          value: 10
        }
      ],
      listData: [],
      listOption: {
        pageNum: 1,
        itemsPerPage: 30,
        sort: '-state'
      },
      lastPage: 0,
      wholePage: 0,
      selectNode: null,
      selectedState: null,
      isHeatmapRun: false,
      isHeatmapStop: false,
      collectionPayload: {}
    }
  },
  watch: {
    sid () {
      this.loadHeatmapGroupList(null, 'domainSearch')
    }
  },
  computed: {
    ...mapState([
      'domain',
      'isDemo'
    ]),
    columns () {
      return [
        {
          text: this.$t('group.column.pageInfo'),
          value: 'info'
        },
        {
          text: this.$t('group.column.status'),
          value: 'state'
        },
        {
          text: this.$t('group.column.startDate'),
          value: 'startDate'
        },
        {
          text: this.$t('group.column.endDate'),
          value: 'endDate'
        },
        {
          text: this.$t('group.column.history'),
          value: 'history'
        }
      ]
    },
    isOldCode () {
      return this.domain.v1LastFoundDate && !this.domain.v2LastFoundDate
    },
    parentSid () {
      return 's' + this.$route.query.sid
    },
    order () {
      return !this.listOption.sort.includes('-')
    },
    computedSort () {
      return this.listOption.sort.replace('-', '')
    },
    isHeatmapExist () {
      if (this.wholePage !== 0 && this.listData.length === 0) {
        return true
      } else {
        return false
      }
    }
  },
  created () {
    this.loadHeatmapGroupList()
  },
  methods: {
    loadHeatmapGroupList (keyword, type) {
      const payload = {
        parentSid: this.parentSid,
        pageId: this.listOption.pageNum,
        pageNum: this.listOption.itemsPerPage,
        sortBy: this.listOption.sort,
        term: this.searchOption,
        text: this.keyword
      }
      if (keyword) {
        payload.text = keyword
      }
      if (this.sid && this.sid !== 'all') {
        payload.sid = this.sid
      }
      if (!this.isLoadedOnce) {
        payload.term = null
        payload.text = null
        this.isLoadedOnce = true
      }
      if (type === 'domainSearch') {
        payload.term = null
        payload.text = null
        this.onClear(true)
      }
      getHeatmapGroupList(payload, true).then(result => {
        for (const item of result.list) {
          item.listUrl = simpleUrl(item.url, this.domain.matchType, true)
        }
        this.listData = normalizeHeatmapListData(result.list, true)
        if (this.listData.length === 0) {
          this.listOption.sort = 'disabled'
          this.$emit('count', 0)
        } else {
          this.$emit('count', result.totalCount)
        }
        this.wholePage = result.totalCount
        this.calcLastPage()
        if (type === 'domainSearch') {
          this.onClear(true)
        }
        this.$emit('searchLength', this.listData.length)
        this.$emit('no-heatmap', (this.listData.length === 0 && !payload.text))
      })
    },
    onHistoryView (item) {
      this.isHistoryView = true

      const startDate = item.installDate ? item.installDate : item.startDate

      this.collectionPayload = {
        aid: item._id,
        parentSid: this.parentSid,
        startDate: format(startDate, 'YYYY-MM-DD'),
        endDate: item.endDate,
        url: item.listUrl
      }
    },
    setPage (page) {
      this.listOption.pageNum = page
      this.loadHeatmapGroupList()
    },
    onViewPerPageOptionChange (value) {
      this.listOption.itemsPerPage = value
      this.listOption.pageNum = 1
      this.loadHeatmapGroupList()
    },
    changeSort (sort) {
      if (this.listOption.sort === '-' + sort) {
        this.listOption.pageNum = 1
        this.listOption.sort = sort
      } else {
        this.listOption.pageNum = 1
        this.listOption.sort = '-' + sort
      }
      // if (this.lastPage > 1) {
      this.loadHeatmapGroupList()
      // } else {
      //   this.sortInList()
      // }
    },
    calcLastPage () {
      if (this.listData.length < this.listOption.itemsPerPage && this.listOption.pageNum === 1) {
        this.lastPage = Math.ceil(this.listData.length / this.listOption.itemsPerPage)
      } else {
        this.lastPage = Math.ceil(this.wholePage / this.listOption.itemsPerPage)
      }
    },
    // sortInList () {
    //   this.listData.sort((a, b) => {
    //     if (typeof a[this.computedSort] === 'string' && typeof b[this.computedSort] === 'string') {
    //       if (this.order) {
    //         return a[this.computedSort].toString().localeCompare(b[this.computedSort].toString())
    //       } else {
    //         return b[this.computedSort].toString().localeCompare(a[this.computedSort].toString())
    //       }
    //     } else {
    //       if (this.order) {
    //         return a[this.computedSort] > b[this.computedSort] ? 1 : -1
    //       } else {
    //         return b[this.computedSort] > a[this.computedSort] ? 1 : -1
    //       }
    //     }
    //   })
    // },
    isHeatmapSet () {
      this.onHeatmapRegister(this.selectNode.data, this.selectNode.index)
    },
    onHeatmapRegister (item, index) {
      this.selectNode = {
        data: item,
        index: index
      }
      const targetNode = this.getTargetNode(index)
      const pageData = {
        baApply: targetNode,
        gUrlId: targetNode.gUrlId,
        pathname: targetNode.pathname,
        url: targetNode.url,
        listUrl: targetNode.listUrl
      }
      // targetNode.heatmapState = HEATMAP_STATE.RUN
      this.isHeatmapRun = false
      this.isHeatmapStop = false

      this.$emit('onHeatmapRegistration', pageData)
    },
    onHeatmapStop () {
      const targetNode = this.getTargetNode(this.selectNode.index)
      targetNode.heatmapState = HEATMAP_STATE.STOP
      this.isHeatmapStop = false
    },
    getTargetNode (index) {
      if (index === null) {
        return null
      }
      return this.listData[this.selectNode.index]
    },
    stateUpdate (run) {
      const baApply = this.getTargetNode(this.selectNode.index)
      if (baApply) {
        if (baApply.state === 'endByAnalysisDate') {
          this.onHeatmapRegister(this.selectNode.data, this.selectNode.index)
        } else {
          const baAid = baApply._id
          if (run && (baApply.state === 'stop')) {
            updateBaApply({ id: baAid, parentSid: baApply.parentSid, sid: baApply.sid, state: 'run' }, true).then((resp) => {
              this.isHeatmapRun = false
              this.loadHeatmapGroupList()
            })
          } else if (!run && (baApply.state === 'run' || baApply.state === 'error' || baApply.state === 'stopByDomainCreditEmpty' || baApply.state === 'stopByUserCreditEmpty' || baApply.state === 'stopByJamPvEmpty')) {
            updateBaApply({ id: baAid, state: 'stop' }, true).then((resp) => {
              this.isHeatmapStop = false
              this.loadHeatmapGroupList()
            })
          }
        }
      } else {
        this.onHeatmapRegister(this.selectNode.data, this.selectNode.index)
      }
    },
    onClear (domainSearch) {
      // this.$emit('onChangeSid', 'all')
      if (domainSearch) {
        this.listOption = {
          pageNum: 1,
          itemsPerPage: 30,
          sort: '-state'
        }
      }
      this.$emit('onChangeSearchOption', 'title')
      this.$emit('onChangeKeyword', '')
      this.$emit('onChangeSearchedKeyword', '')
    }
  }
}
</script>
<style lang="scss" scoped>
.list {
  height: 100%;
  position: relative;

  .tbody {
    height: calc(100% - 115px);
    overflow-x: hidden;
    overflow-y: scroll;
    padding-bottom: 10px;
  }
  .shadow {
    &::after {
      content: "";
      position: absolute;
      z-index: 5;
      bottom: 50px;
      padding: 0 10px;
      left: -10px;
      display: block;
      width: 100%;
      height: 6px;
      opacity: 0.06;
      background-image: linear-gradient(to bottom, rgba(0, 0, 0, 0), #000);
    }
  }
  table {
    position: relative;
    width: 100%;

    thead {
      tr {
        border-radius: 7px;
        th {
          height: 39px;
          font-size: 13px;
          font-weight: normal;
          background: #333333;
          border-left: 1px solid #333333;
          color: #ffffff;
          padding: 9px 0 7px 0;
          vertical-align: bottom;
          text-align: left;
          line-height: 20px;
          transition: background-color 0.15s ease;
          white-space: nowrap;

          .icon {
            vertical-align: middle;
            margin: -1px 5px 1px 0;
            cursor: pointer;
          }

          &.gap {
            padding: 0;
            width: 25px;
            border-bottom: none;
          }

          .sort {
            display: inline-block;
            width: 14px;
            height: 18px;
            padding: 1px 0;
            margin-top: -2px;
            margin-left: 2px;
            vertical-align: middle;
            border-radius: 4px;
            transition: background-color 0.15s ease,
            box-shadow 0.15s ease;

            i.icon {
              float: left;
              opacity: 0.2;
              transition: opacity 0.15s ease;
            }

            &.gap {
              padding: 0;
              width: 25px;
              border-bottom: none;
            }

            &.active {
              &.asc {
                .icon--sort-asc {
                  opacity: 1;
                }
              }

              &.desc {
                .icon--sort-desc {
                  opacity: 1;
                }
              }
            }

            &.active {
              background-color: #272727;
              box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.146);
            }
          }
          &.th-state {
            text-align: center;
          }
          &.th-regDate {
            text-align: right;
          }

          &.hover {
            .sort {
              background-color: #272727;
              box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.146);
            }
          }

          &:first-child {
            border-radius: 7px 0 0 7px;
            border-left: 0;
          }

          &:last-child {
            border-radius: 0 7px 7px 0;
          }
        }
      }
    }

    tbody {
      tr {
        position: relative;
        height: 1px;

        td {
          height: 100%;
          text-align: left;
          font-size: 13px;
          line-height: 1.31;
          letter-spacing: normal;
          color: #444;
          word-break: break-all;
          border-bottom: 1px solid #ebebeb;
          padding: 20px 0;

          .td-data {
            position: relative;
            height: 100%;
            padding: 17px 15px 17px 15px;
            box-sizing: border-box;

            &.checkbox {
              padding-left: 34px;
            }

            .ui_checkbox {
              position: absolute;
              top: 15.5px;
              left: 8px;
              margin-left: 2px;
              margin-right: 1px;
            }
            &/deep/ .icon--heatmap-setting {
              top: 25%;
            }
          }
          .page-td {
            display: flex;
            align-items: center;

            .favorite {
              flex-basis: 39px;
              flex-grow: 0;
              flex-shrink: 0;
              text-align: center;

              .bookmark-button {
                &:disabled /deep/ .button {
                  background-color: #f4f4f4;
                  opacity: 0.6;
                }

                &:hover {
                  & /deep/ .tooltip {
                    transform: translate3d(0, calc(-100% - 5px - 3px), 0);
                  }
                }

                & /deep/ .tooltip {
                  left: -5px;
                  right: auto;
                  top: 0;
                  transform: translate3d(0, calc(-100% - 5px - 3px + 10px), 0);

                  &:after {
                    left: calc(20px - 3.5px);
                    top: auto;
                    bottom: -3px;
                    transform: rotate(135deg);
                  }
                }
              }
            }

            .title {
              flex-grow: 1;
              flex-shrink: 1;
              min-width: 0;

              .name {
                color: #444444;
                margin-bottom: 4px;

                .before {
                  display: inline-block;
                  overflow: hidden;
                  white-space: nowrap;
                  text-overflow: ellipsis;
                  min-width: 0;
                  vertical-align: middle;
                  padding-bottom: 2px;
                }

                .after {
                  display: inline-block;
                  // width: 35px;
                  vertical-align: middle;
                  padding-bottom: 2px;
                }
              }

              .url {
                display: flex;
                .url__wrap {
                  display: flex;
                  position: absolute;
                  left: 20px;
                  top: 3px;
                  .after {
                    display: inline-block;
                    width: 50px;
                  }
                  .before {
                    display: inline-block;
                    overflow: hidden;
                    white-space: nowrap;
                    text-overflow: ellipsis;
                    min-width: 0;
                  }
                }

                a {
                  position: relative;
                  font-size: 12px;
                  line-height: 1;
                  color: #888888;
                }

                i.icon {
                  margin-right: 2px;
                }
              }
            }
          }

          &.gap {
            width: 25px;
            border-bottom: none;
          }
          &.td-regDate {
            text-align: right;

            .td-data {
              margin-right: 20px;
              padding-right: 6px;
            }
          }
          &.td-date {
            padding-left: 5px;
            &.noDate {
              padding-left: 10px;
            }
          }
          &.td-history {
            padding-left: 20px;
          }
          .history {
            height: 34px;
            display: flex;
            padding: 9px 18px;
            justify-content: center;
            align-items: center;
            border: 1px solid #56AB00;
            color: #4A8500;
            text-align: center;
            font-size: 14px;
            font-style: normal;
            font-weight: 600;
            border-radius: 61px;
          }
          &.td-heatmap {
            position: relative;
            text-align: center;
            &.disabled {
              pointer-events: none;
              opacity: 0.5;
            }
            .td-data {
              .icon--heatmap-state {
                position: relative;
                padding-right: 50px;
              }
              .icon--heatmap-setting {
                position: absolute;
                top: 50%;
                transform: translate(0, -50%);
                right: 20px;
              }
            }
          }
        }
      }
    }
  }
  .no-data {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    text-align: center;

    .no-data-title {
      font-size: 13px;
      font-weight: 700;
      line-height: 1;
      color: #2f2f2f;
      margin: 10px 0 15px;
      &.search {
        font-size: 20px;
      }
    }

    .no-data-description {
      font-size: 14px;
      color: #767676;
    }

    .no-data-keyword {
      margin-top: 30px;
      font-size: 15px;
      font-weight: 500;
      color: #ff8400;
    }
  }
}
.list-bottom {
  z-index: 2;
  position: absolute;
  width: 100%;
  bottom: 0;
  left: 0;
  height: 49px;
  border-top: 1px solid rgba(0, 0, 0, 0.16);
  // border-left: 1px solid #ebebeb;
  border-radius: 0 0 14px 14px;

  .page-navigation {
    float: left;
    margin-left: 8px;
  }

  &.shadow {
    // box-shadow: 0 -1px 5px 0px rgba(0,0,0,.16);
  }

  .view-per-page {
    float: right;
    font-size: 12px;
    color: #767676;
    padding: 0px 26px 0 0;
    margin-top: -5px;

    .ui_select {
      padding-left: 5px;
    }
  }
}
</style>
